import { Injectable, OnDestroy } from '@angular/core';
import { isEqual } from 'lodash';
import { BehaviorSubject, EMPTY, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { v4 } from 'uuid';

import {
  DataConfiguration,
  IProcessedValidationConfig,
  IValidationConfig,
  preprocessValidationConfigs,
} from '@assess/shared/dynamic-components';
import { logger } from '@assess/shared/logger';
import { Nullable } from '@assess/shared/type-utilities';

import { IDisposable } from './disposable.interface';
import { ManagedDynamicContextBindings } from './managed-dynamic-context-bindings';
import { NotifyPropertyChanged, ofPropertyNames } from './notify-property-changed.base';
import { SubscriptionManager } from './subscription-manager';
import { validateForm } from '../../functions/validate-form';
import { IDynamicContext, IDynamicContextBindings } from '../../types/dynamic-context.interface';
import { DynamicFormBuilderService } from '../dynamic-form-builder.service';

interface IManagedDynamicContext extends IDynamicContext {
  model: Nullable<any>;
  validationDefinitions: Nullable<IValidationConfig[]>;
}

type ContextInter = IManagedDynamicContext & IDynamicContextBindings;

// Note that a new instance should be created for each root component - do not import into a module or add providedIn!
// Instead, add to a component's `providers` configuration.
@Injectable()
export class ManagedDynamicContext
  extends NotifyPropertyChanged<IManagedDynamicContext>
  implements IDisposable, IManagedDynamicContext, OnDestroy
{
  private _dataDefinition: Nullable<DataConfiguration>;
  private _model: Nullable<any>;
  private _validationDefinitions: Nullable<IValidationConfig[]>;
  private readonly _bindings = new ManagedDynamicContextBindings();
  private readonly _uuid = v4();
  private readonly instanceLogger = logger.child({ context: ManagedDynamicContext.name });
  private readonly modelChanged = new BehaviorSubject<Nullable<any>>(undefined);
  private readonly subscriptionManager = new SubscriptionManager();
  private validationConfigs: Nullable<Map<string, IProcessedValidationConfig>>;

  readonly modelChanged$ = this.modelChanged.asObservable();

  get bindings() {
    return this._bindings;
  }

  get dataDefinition() {
    return this._dataDefinition;
  }
  set dataDefinition(value) {
    if (value !== this._dataDefinition) {
      this._dataDefinition = value;
      this.notifyPropertyChanged('dataDefinition');
    }
  }

  get model() {
    return this._model;
  }
  set model(value) {
    if (value !== this._model) {
      this._model = value;
      this.notifyPropertyChanged('model');
    }
  }

  get validationDefinitions() {
    return this._validationDefinitions;
  }
  set validationDefinitions(value) {
    if (value !== this._validationDefinitions) {
      this._validationDefinitions = value;
      this.notifyPropertyChanged('validationDefinitions');
    }
  }

  get uuid() {
    return this._uuid;
  }

  constructor(private readonly formBuilder: DynamicFormBuilderService) {
    super();

    this.configurePropertyChanged();
  }

  validate() {
    if (!this.bindings.editable) {
      this.instanceLogger.warn(
        {
          data: { uuid: this.uuid, method: ManagedDynamicContext.prototype.validate.name },
        },
        'No form exists'
      );

      return EMPTY;
    }

    if (!this.validationConfigs?.size) {
      this.instanceLogger.warn(
        {
          data: { uuid: this.uuid, method: ManagedDynamicContext.prototype.validate.name },
        },
        'No validation configs exist'
      );

      return EMPTY;
    }

    return validateForm(this.bindings.editable, this.validationConfigs);
  }

  dispose() {
    super.dispose();

    this.modelChanged.complete();
    this.subscriptionManager.dispose();
    this.bindings.dispose();
  }

  ngOnDestroy() {
    this.dispose();
  }

  private configureValidation() {
    const validationKey = 'formValidation';

    this.subscriptionManager.delete(validationKey);

    if (!this.bindings.editable) {
      return;
    }

    const validation = this.bindings.editable.valueChanges
      .pipe(
        debounceTime(0),
        distinctUntilChanged(isEqual),
        switchMap(() => this.validate()),
        tap((errors) => (this.bindings.errors = errors))
      )
      .subscribe();

    this.subscriptionManager.set(validationKey, validation);
  }

  private configureModelChanged() {
    const modelChangedKey = 'modelChanged';

    this.subscriptionManager.delete(modelChangedKey);

    if (!this.bindings.editable) {
      return;
    }

    const modelChange = this.bindings.editable.valueChanges
      .pipe(
        debounceTime(0),
        distinctUntilChanged(isEqual),
        tap((value) => this.modelChanged.next(value))
      )
      .subscribe();

    this.subscriptionManager.set(modelChangedKey, modelChange);
  }

  // NOTE: propertyChanged$ handlers are managed via Subject and do not need unsubscribed
  private configurePropertyChanged() {
    // Validation definition processing
    this.propertyChanged$
      .pipe(
        ofPropertyNames('validationDefinitions'),
        switchMap(() => {
          return this.validationDefinitions
            ? preprocessValidationConfigs(this.validationDefinitions)
            : Promise.resolve(new Map());
        }),
        tap((validationConfigs) => (this.validationConfigs = validationConfigs))
      )
      .subscribe();

    // Build form
    this.propertyChanged$
      .pipe(
        ofPropertyNames('dataDefinition'),
        switchMap(() => (this.dataDefinition ? this.formBuilder.build(this.dataDefinition, this.model) : EMPTY)),
        tap((form) => (this.bindings.editable = form))
      )
      .subscribe();

    // Form value changes support
    merge(this.propertyChanged$, this.bindings.propertyChanged$)
      .pipe(
        ofPropertyNames<ContextInter>('editable', 'validationDefinitions'),
        tap(() => this.configureValidation())
      )
      .subscribe();

    // Handle model
    merge(this.propertyChanged$, this.bindings.propertyChanged$)
      .pipe(
        ofPropertyNames<ContextInter>('editable', 'model'),
        tap(() => {
          if (this.bindings.editable) {
            this.bindings.editable.patchValue(this.model);
          }
        }),
        tap(() => this.configureModelChanged()),
        tap(() => this.modelChanged.next(this.bindings.editable?.value ?? this.model))
      )
      .subscribe();
  }
}
